import { useCallback, useEffect, useState } from 'react'
import {
  chargerJournalCommandes, chargerLiaison, COMMANDES, envoyerCommande, etatTraccar, libelleParametresCommande, LIBELLE_STATUT_COMMANDE, MOTIFS_REFUS, MOTS_DE_CONFIRMATION_MOTEUR,
  nommerBoitier, phraseCommande, phraseSynchro, synchroniserBoitier, type CommandeJournal, type EtatTraccar, type Liaison, type TypeCommande,
} from '../../api/alertes'
import { nomBoitier } from '../../api/gps'
import type { Boitier, DonneesOps } from '../../api/types'
import { useDroits } from '../Contexte'
import { dateCourte, heure } from '../Format'
import { Icone } from '../Icone'
import { Modale } from '../Modale'

/**
 * Fiche d'un boîtier (0052) : son NOM (le même ici, sur la carte, dans les alertes et dans Traccar), et le panneau « Commandes ».
 *
 * Les commandes passent par l'edge function `gps-commande` — jamais le navigateur vers Traccar. La base décide (rôle,
 * appartenance du boîtier à CETTE organisation) ; couper le moteur est réservé au propriétaire, exige la confirmation explicite, un
 * véhicule à l'arrêt (moins de 5 km/h, position de moins de 3 minutes) et est journalisé. Propriétaire et dispatch envoient les autres
 * commandes ; le comptable ne voit que le journal.
 */
export function FicheBoitier({
  boitier,
  donnees,
  surFermer,
  surRafraichir,
  surNotifier,
}: {
  boitier: Boitier
  donnees: DonneesOps
  surFermer: () => void
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const droits = useDroits()
  const { apercu, fixturesAlertes } = droits
  const nomAffiche = nomBoitier(boitier, donnees.vehicules)
  const gere = droits.peut('boitiers')
  const proprietaire = droits.peut('moteur_boitiers')

  const [nom, setNom] = useState(boitier.label ?? '')
  const [retourNom, setRetourNom] = useState<string | null>(null)
  const [liaison, setLiaison] = useState<Liaison | null>(null)
  const [traccar, setTraccar] = useState<EtatTraccar | null>(fixturesAlertes?.suivi ?? null)
  const [journal, setJournal] = useState<CommandeJournal[]>(fixturesAlertes?.journal[boitier.id] ?? [])
  const [type, setType] = useState<TypeCommande>('position_maintenant')
  const [secondes, setSecondes] = useState(30)
  const [decalage, setDecalage] = useState(0)
  const [apn, setApn] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [enCoursNom, setEnCoursNom] = useState(false)
  const [retourCommande, setRetourCommande] = useState<{ ok: boolean; texte: string } | null>(null)

  const chargerEtat = useCallback(async () => {
    if (apercu) return
    const [l, j] = await Promise.all([chargerLiaison(boitier.id).catch(() => null), chargerJournalCommandes(boitier.id).catch(() => null)])
    setLiaison(l)
    if (j) setJournal(j)
  }, [boitier.id, apercu])

  useEffect(() => {
    void chargerEtat()
    if (!apercu) void etatTraccar().then(setTraccar)
    /* Le journal avance tout seul (accusé de réception du boîtier) tant que la fiche est ouverte. */
    const t = setInterval(() => void chargerEtat(), 10_000)
    return () => clearInterval(t)
  }, [chargerEtat, apercu])

  /** Une opération de nom : en aperçu on n'envoie rien ; sinon on affiche la phrase de synchronisation avec Traccar. */
  async function operationNom(action: () => Promise<Parameters<typeof phraseSynchro>[0]>, remplacer?: [string, string]) {
    if (apercu) {
      surNotifier('Aperçu : rien n’est enregistré (données fictives).')
      return
    }
    setRetourNom(null)
    setEnCoursNom(true)
    try {
      const phrase = phraseSynchro(await action())
      setRetourNom(remplacer ? phrase.replace(remplacer[0], remplacer[1]) : phrase)
      await surRafraichir()
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCoursNom(false)
    }
    await chargerEtat()
  }

  const def = COMMANDES.find((c) => c.type === type)!
  const parametresCommande = (): Record<string, unknown> => {
    if (type === 'intervalle_positions') return { secondes }
    if (type === 'fuseau') return { decalage_minutes: decalage }
    if (type === 'apn') return { apn: apn.trim() }
    if (type === 'moteur_couper') return { confirmation }
    return {}
  }
  const invalide =
    (type === 'intervalle_positions' && !(Number.isInteger(secondes) && secondes >= 10 && secondes <= 3600)) ||
    (type === 'fuseau' && !(Number.isInteger(decalage) && decalage >= -720 && decalage <= 840 && decalage % 15 === 0)) ||
    (type === 'apn' && !/^[A-Za-z0-9._-]{1,40}$/.test(apn.trim())) ||
    (type === 'moteur_couper' && confirmation !== MOTS_DE_CONFIRMATION_MOTEUR)

  async function envoyer() {
    setRetourCommande(null)
    if (apercu) {
      surNotifier('Aperçu : aucune commande n’est envoyée (données fictives).')
      return
    }
    if (def.dangereuse) {
      const phrase =
        type === 'moteur_couper'
          ? `COUPER LE MOTEUR de « ${nomAffiche} » ?\n\nLe véhicule ne pourra plus redémarrer tant que vous n’aurez pas rétabli le moteur. L’action est journalisée à votre nom et refusée si le véhicule roule.`
          : `Rétablir le moteur de « ${nomAffiche} » ?`
      if (!window.confirm(phrase)) return
    }
    setEnvoi(true)
    try {
      const r = await envoyerCommande(boitier.id, type, parametresCommande())
      setRetourCommande({ ok: true, texte: r.message ?? 'Commande envoyée.' })
      setConfirmation('')
    } catch (e) {
      setRetourCommande({ ok: false, texte: phraseCommande(e) })
    }
    setEnvoi(false)
    await chargerEtat()
  }

  const traccarAbsent = traccar !== null && !traccar.configure
  const aSynchroniser = liaison !== null && ((liaison.nomEnvoye ?? '') !== (boitier.label ?? '') || liaison.erreur !== null)

  return (
    <Modale titre={`Boîtier ${nomAffiche}`} ouverte surFermer={surFermer} large>
      <div style={{ display: 'grid', gap: 22 }}>
        {/* ───────────── Nom ───────────── */}
        <section>
          <h4>Nom du boîtier</h4>
          <p className="t-muted" style={{ margin: '4px 0 10px' }}>
            Ce nom s’affiche partout — carte, listes, alertes — et est enregistré à l’identique dans Traccar pour retrouver le même nom des deux côtés.
            Identifiant : <span className="mono">{boitier.unique_id}</span>.
          </p>
          <div className="ligne-champ">
            <div className="field">
              <label htmlFor="fiche-nom">Nom (60 caractères au plus)</label>
              <input
                id="fiche-nom"
                value={nom}
                maxLength={60}
                onChange={(e) => setNom(e.target.value)}
                disabled={!gere}
                placeholder="ex. Yaris de test, Moto livraison 1"
              />
            </div>
            {gere && (
              <>
                <button
                  className="btn btn-gold btn-sm"
                  type="button"
                  onClick={() => void operationNom(() => nommerBoitier(boitier.id, nom))}
                  disabled={enCoursNom || nom.trim() === (boitier.label ?? '')}
                >
                  Enregistrer le nom
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  type="button"
                  onClick={() => void operationNom(() => synchroniserBoitier(boitier.id), ['Nom enregistré', 'Synchronisation faite'])}
                  disabled={enCoursNom}
                  title="Aligne Traccar sur ce nom (crée le boîtier dans Traccar s’il n’y est pas)"
                >
                  <Icone nom="refresh" /> Synchroniser avec Traccar
                </button>
              </>
            )}
          </div>
          {retourNom && (
            <div className="bandeau" role="status" style={{ marginTop: 10, marginBottom: 0 }}>
              {retourNom}
            </div>
          )}
          {liaison && (
            <p className="t-muted" style={{ marginTop: 8 }}>
              {liaison.erreur
                ? `Traccar : synchronisation à refaire (${liaison.erreur === 'non_configure' ? 'Traccar n’est pas encore configuré' : liaison.erreur.replace('traccar_', '')}).`
                : liaison.synchroniseLe
                  ? `Traccar : synchronisé le ${dateCourte(liaison.synchroniseLe)} à ${heure(liaison.synchroniseLe)} sous « ${liaison.nomEnvoye ?? ''} ».`
                  : 'Traccar : pas encore synchronisé.'}
              {aSynchroniser && !liaison.erreur ? ' Le nom a changé depuis : synchronisez.' : ''}
            </p>
          )}
        </section>

        {/* ───────────── Commandes ───────────── */}
        <section>
          <h4>Commandes</h4>
          {traccar?.simulation && (
            <div className="bandeau" style={{ margin: '8px 0' }}>
              <span className="badge b-violet">Mode simulation</span> Aucune commande n’est réellement envoyée au boîtier ; le journal l’indique.
            </div>
          )}
          {traccarAbsent && (
            <div className="bandeau alerte" style={{ margin: '8px 0' }}>
              <b>Traccar non configuré.</b> Les commandes seront disponibles dès que le serveur Traccar aura son jeton d’accès (mise en service par Yiriwa). Le
              nom du boîtier s’enregistre dès maintenant et sera aligné ensuite.
            </div>
          )}
          {!gere ? (
            <p className="t-muted" style={{ marginTop: 6 }}>{droits.refus} Seuls le propriétaire et le dispatch envoient des commandes ; vous voyez le journal ci-dessous.</p>
          ) : (
            <>
              <div className="cartes-commande" role="radiogroup" aria-label="Commande à envoyer">
                {COMMANDES.map((c) => {
                  const interdit = c.proprietaire && !proprietaire
                  return (
                    <button
                      key={c.type}
                      type="button"
                      role="radio"
                      aria-checked={type === c.type}
                      className={`commande${type === c.type ? ' on' : ''}${c.dangereuse ? ' danger' : ''}`}
                      onClick={() => {
                        setType(c.type)
                        setRetourCommande(null)
                      }}
                    >
                      {c.libelle}
                      {interdit && <span className="t-muted">Propriétaire seulement</span>}
                    </button>
                  )
                })}
              </div>
              <div className="detail-commande">
                <p className="t-muted" style={{ margin: '0 0 8px' }}>{def.aide}</p>
                {type === 'intervalle_positions' && (
                  <div className="field">
                    <label htmlFor="cmd-secondes">Une position toutes les (secondes)</label>
                    <input
                      id="cmd-secondes"
                      type="number"
                      min={10}
                      max={3600}
                      step={1}
                      value={Number.isFinite(secondes) ? secondes : ''}
                      onChange={(e) => setSecondes(e.target.value === '' ? Number.NaN : Number(e.target.value))}
                    />
                    <div className="t-muted">De 10 à 3600. 30 à 60 s suffisent pour de la flotte urbaine ; 10 à 20 s pour la « vitesse dangereuse ».</div>
                  </div>
                )}
                {type === 'fuseau' && (
                  <div className="field">
                    <label htmlFor="cmd-decalage">Décalage par rapport à l’heure universelle (minutes)</label>
                    <input
                      id="cmd-decalage"
                      type="number"
                      min={-720}
                      max={840}
                      step={15}
                      value={Number.isFinite(decalage) ? decalage : ''}
                      onChange={(e) => setDecalage(e.target.value === '' ? Number.NaN : Number(e.target.value))}
                    />
                    <div className="t-muted">Mali : 0. Par pas de 15 minutes, de -720 à 840.</div>
                  </div>
                )}
                {type === 'apn' && (
                  <div className="field">
                    <label htmlFor="cmd-apn">APN</label>
                    <input id="cmd-apn" value={apn} maxLength={40} onChange={(e) => setApn(e.target.value)} placeholder="ex. orangeml.net" autoComplete="off" />
                    <div className="t-muted">Lettres, chiffres, point, tiret ou souligné (40 au plus). Ni nom d’utilisateur ni mot de passe ici.</div>
                  </div>
                )}
                {type === 'moteur_couper' && (
                  <div className="bandeau erreur" style={{ marginBottom: 10 }}>
                    <b>
                      <Icone nom="alert" /> Action dangereuse.
                    </b>{' '}
                    Le moteur de « {nomAffiche} » ne redémarrera plus tant qu’il n’est pas rétabli. Refusée si le véhicule roule (5 km/h ou plus) ou si sa dernière
                    position a plus de 3 minutes. Journalisée à votre nom.
                    <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
                      <label htmlFor="cmd-confirmation">Tapez {MOTS_DE_CONFIRMATION_MOTEUR} pour confirmer</label>
                      <input
                        id="cmd-confirmation"
                        value={confirmation}
                        onChange={(e) => setConfirmation(e.target.value)}
                        autoComplete="off"
                        disabled={!proprietaire}
                      />
                    </div>
                  </div>
                )}
                <div className="actions-ligne" style={{ marginTop: 10 }}>
                  <button
                    className={`btn btn-sm ${def.dangereuse ? 'btn-danger' : 'btn-gold'}`}
                    type="button"
                    onClick={() => void envoyer()}
                    disabled={envoi || invalide || (traccarAbsent && !apercu) || (def.proprietaire && !proprietaire)}
                  >
                    {envoi ? 'Envoi…' : def.libelle}
                  </button>
                </div>
                {retourCommande && (
                  <div className={`bandeau${retourCommande.ok ? '' : ' erreur'}`} role="status" style={{ marginTop: 10, marginBottom: 0 }}>
                    {retourCommande.texte}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* ───────────── Journal ───────────── */}
        <section>
          <h4>Journal des commandes</h4>
          <p className="t-muted" style={{ margin: '4px 0 10px' }}>
            Qui, quand, quoi, et ce qui s’est passé : demandée, envoyée, acquittée par le boîtier, hors ligne (en attente de sa reconnexion), refusée ou en
            échec. Ce journal ne se modifie ni ne s’efface.
          </p>
          {journal.length === 0 ? (
            <div className="no-hit">Aucune commande pour ce boîtier.</div>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Quand</th>
                    <th>Commande</th>
                    <th>Par</th>
                    <th>État</th>
                  </tr>
                </thead>
                <tbody>
                  {journal.map((c) => {
                    const def2 = COMMANDES.find((x) => x.type === c.type)
                    const s = LIBELLE_STATUT_COMMANDE[c.statut]
                    const motif = typeof c.detail.motif === 'string' ? MOTIFS_REFUS[c.detail.motif] ?? c.detail.motif : null
                    const reponse = typeof c.detail.reponse === 'string' ? c.detail.reponse : null
                    return (
                      <tr key={c.commandeId}>
                        <td>
                          {dateCourte(c.creeLe)} {heure(c.creeLe)}
                        </td>
                        <td>
                          <div className="t-strong">{def2?.libelle ?? c.type}</div>
                          <div className="t-muted">{libelleParametresCommande(c.type, c.parametres)}</div>
                        </td>
                        <td>{c.parNom ?? '—'}</td>
                        <td style={{ whiteSpace: 'normal' }}>
                          <span className={`badge ${s.pastille}`}>{s.libelle}</span>
                          {motif && <div className="t-muted">{motif}</div>}
                          {reponse && <div className="t-muted">Réponse : {reponse}</div>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Modale>
  )
}
